import type { FormikErrors, FormikProps } from 'formik'

import { FlowPage, FormItem } from '@monthio/design-system'
import {
	FormBoxInput,
	FormMultipleChoice,
	makeStyles,
} from '@monthio/react-components'
import i18n from 'i18next'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { SmartCheckConfigurationDto } from '../../../types/authenticateSmartcheckSessionDto'
import type {
	DefaultConsumerInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '../types/questionnaire.types'

import { ScrollToViewContainer } from '../../../modulesTemp/shared/ScrollToViewContainer'
import { BoxInputWrapper } from './carCostStep'
import { InputSection } from './shared'

const useInputStyles = makeStyles(() => ({
	root: {
		'& .MuiInputBase-root.Mui-error': {
			position: 'relative',
		},
		'& .MuiFormHelperText-root.Mui-error': {
			position: 'absolute',
			right: '53px',
			width: 'max-content',
			marginTop: '33px',
		},
	},
}))

const isAcquiringExtraHome = (response: string) => response === '0'
const hasExpensesToMultipleHomes = (response: string) => response === '1'

const HomeInformationForm = ({
	formProps,
	caseConfiguration,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
	caseConfiguration?: SmartCheckConfigurationDto
}): JSX.Element => {
	const { values, setFieldValue, setTouched, setSubmitting } = {
		...formProps,
	}
	const { t } = useTranslation()
	const binaryChoices = [
		{
			value: '1',
			label: t('btn-yes'),
		},
		{
			value: '0',
			label: t('btn-no'),
		},
	]
	const classes = useInputStyles()

	useEffect(() => {
		setTouched({})
		setSubmitting(false)
	}, [setSubmitting, setTouched])

	const isHomeReplacement =
		values.homeLenderInputs !== null &&
		values.homeLenderInputs?.acquiringExtraHome !== null &&
		values.homeLenderInputs?.acquiringExtraHome === false
	const hasMultipleHomeExpenses =
		values.homeLenderInputs?.hasExpensesToMultipleHomes == null
			? null
			: values.homeLenderInputs?.hasExpensesToMultipleHomes

	const handleAcquiringExtraHomeClick = useCallback(
		(event) => {
			const isAcquiringExtraHomeResponse = isAcquiringExtraHome(
				event.target.value,
			)
			setFieldValue(event.target.name, isAcquiringExtraHomeResponse)

			if (isAcquiringExtraHomeResponse) {
				setFieldValue(
					'homeLenderInputs.hasExpensesToMultipleHomes',
					null,
					false,
				)
			}
		},
		[setFieldValue],
	)

	const handleHasExpensesToMultipleHomesClick = useCallback(
		(event) => {
			setFieldValue(
				event.target.name,
				hasExpensesToMultipleHomes(event.target.value),
			)
		},
		[setFieldValue],
	)

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-homeInformationStep-title')}
				description={t('consumerInputs-homeInformationStep-subtitle')}
			/>
			<ScrollToViewContainer>
				<FormItem
					label={t(
						'consumerInputs-homeInformationStep-acquiringExtraHome-text',
					)}
					isRequired
					requiredText={t('required')}
				>
					<FormMultipleChoice
						id="homeLenderInputs.acquiringExtraHome"
						fieldName="homeLenderInputs.acquiringExtraHome"
						choices={binaryChoices}
						onClick={handleAcquiringExtraHomeClick}
					/>
				</FormItem>
			</ScrollToViewContainer>
			{isHomeReplacement ? (
				<InputSection>
					<ScrollToViewContainer>
						<FormItem
							label={t(
								'consumerInputs-homeInformationStep-hasExpensesToMultipleHomes-text',
							)}
							isRequired
							requiredText={t('required')}
						>
							<FormMultipleChoice
								id="homeLenderInputs.hasExpensesToMultipleHomes"
								fieldName="homeLenderInputs.hasExpensesToMultipleHomes"
								choices={binaryChoices}
								onClick={handleHasExpensesToMultipleHomesClick}
							/>
						</FormItem>
					</ScrollToViewContainer>
					{hasMultipleHomeExpenses === false &&
					caseConfiguration?.shouldUseNewBudgetsFeature ? (
						<ScrollToViewContainer>
							<FormItem
								label={t('consumerInputs-homeInformationStep-insurance-text')}
								isRequired
								requiredText={t('required')}
							>
								<BoxInputWrapper>
									<FormBoxInput
										id="homeLenderInputs.insurance"
										type="tel"
										currencyInputProps={{
											allowNegativeValue: false,
											allowDecimals: false,
										}}
										className={classes.root}
									/>
								</BoxInputWrapper>
							</FormItem>
						</ScrollToViewContainer>
					) : null}
				</InputSection>
			) : null}
		</FlowPage.Content>
	)
}

const HomeInformationFormValidation = async ({
	values,
	errors,
	caseConfiguration,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	if (values.homeLenderInputs == null) {
		return errors
	}

	const requiredFieldText = i18n.t('validation-fieldRequired')

	const isReplacement = values.homeLenderInputs.acquiringExtraHome === false
	const hasMultipleHomes =
		values.homeLenderInputs.hasExpensesToMultipleHomes === true

	if (values.homeLenderInputs.acquiringExtraHome == null) {
		errors.homeLenderInputs = {
			...errors.homeLenderInputs,
			acquiringExtraHome: requiredFieldText,
		}
	}

	if (
		isReplacement &&
		values.homeLenderInputs.hasExpensesToMultipleHomes == null
	) {
		errors.homeLenderInputs = {
			...errors.homeLenderInputs,
			hasExpensesToMultipleHomes: requiredFieldText,
		}
	}

	if (
		isReplacement &&
		!hasMultipleHomes &&
		caseConfiguration?.shouldUseNewBudgetsFeature &&
		values.homeLenderInputs.insurance == null
	) {
		errors.homeLenderInputs = {
			...errors.homeLenderInputs,
			insurance: requiredFieldText,
		}
	}

	return errors
}

export const HomeInformationStep: QuestionnaireStep = {
	validation: HomeInformationFormValidation,
	component: HomeInformationForm,
}
